/**
 * Estado del tracker en un instante: dónde está el trineo, a qué apunta la
 * cuenta atrás y cuándo pasará por la zona del visitante.
 *
 * El calendario de la campaña (llegadas y fases) vive en `season.ts`; aquí solo
 * está la lectura que se hace de él desde un reloj concreto. Todo son funciones
 * puras — mismas entradas, mismas salidas—, que es lo que permite que el
 * servidor pinte el HTML indexable y el cliente lo recalcule cada segundo sin
 * discrepancias de hidratación.
 */

import { CUMULATIVE_COUNTRIES, ROUTE } from './route'
import {
  DAY_MS,
  HOUR_MS,
  VISITOR_WINDOW_END_HOUR,
  VISITOR_WINDOW_START_HOUR,
  arrivalsFor,
  localMomentToUtcMs,
  phaseAt,
  seasonFor,
} from './season'
import type {
  RouteLegKey,
  RouteStop,
  SleighPosition,
  TrackerPhase,
  TrackerSeason,
  TrackerState,
  VisitorWindow,
} from './types'

export {
  DEFAULT_UTC_OFFSET_HOURS,
  LIFTOFF_WINDOW_HOURS,
  VISITOR_WINDOW_END_HOUR,
  VISITOR_WINDOW_START_HOUR,
  arrivalsFor,
  seasonFor,
  stopArrivalMs,
} from './season'

/**
 * Desde dónde se mira el tracker: el instante y la zona horaria del visitante.
 *
 * Los dos datos viajan siempre juntos porque solos no significan nada — «las
 * 22:00» solo ubica el trineo si se sabe de qué huso—, y tenerlos en un objeto
 * evita el error de pasarlos al revés.
 */
export interface ViewerClock {
  /** Instante actual, epoch ms UTC. */
  nowMs: number
  /**
   * Desfase de la zona horaria del visitante **en diciembre**. El servidor usa
   * el de la zona por defecto del sitio; el cliente lo sustituye por el real
   * tras montar. Nunca se pide la ubicación.
   */
  utcOffsetHours: number
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value))
}

/**
 * La temporada relevante ahora mismo. Del 1 de enero al 31 de diciembre es
 * siempre la del año en curso: hasta Nochebuena se prepara, y del 25 al 31 se
 * muestra el resumen del viaje recién terminado.
 */
export function resolveSeason(clock: ViewerClock): TrackerSeason {
  return seasonFor(new Date(clock.nowMs).getUTCFullYear())
}

export function resolvePhase(clock: ViewerClock, season: TrackerSeason): TrackerPhase {
  return phaseAt(clock.nowMs, season)
}

/**
 * Posición del trineo interpolando entre las dos paradas que rodean al
 * instante actual. Sin envoltura por el antimeridiano: la ruta va de este a
 * oeste sin cruzarlo (ver la nota de `route.ts` sobre Kiritimati).
 */
export function interpolatePosition(
  arrivals: readonly number[],
  clock: ViewerClock
): { index: number; position: SleighPosition } {
  const { nowMs } = clock
  if (nowMs <= arrivals[0]) {
    return { index: -1, position: { lon: ROUTE[0].lon, lat: ROUTE[0].lat } }
  }

  const lastIndex = ROUTE.length - 1
  if (nowMs >= arrivals[lastIndex]) {
    return { index: lastIndex, position: { lon: ROUTE[lastIndex].lon, lat: ROUTE[lastIndex].lat } }
  }

  let index = 0
  for (let i = 0; i < ROUTE.length; i += 1) {
    if (arrivals[i] <= nowMs) index = i
    else break
  }

  const from = ROUTE[index]
  const to = ROUTE[index + 1]
  const span = arrivals[index + 1] - arrivals[index]
  const ratio = span > 0 ? clamp01((nowMs - arrivals[index]) / span) : 0

  return {
    index,
    position: {
      lon: from.lon + (to.lon - from.lon) * ratio,
      lat: from.lat + (to.lat - from.lat) * ratio,
    },
  }
}

/** Franja aproximada de paso por la zona del visitante, en epoch ms UTC. */
export function visitorWindowFor(season: TrackerSeason, clock: ViewerClock): VisitorWindow {
  const bounds = (localHour: number) =>
    localMomentToUtcMs({ year: season.year, dayOfMonth: 24, localHour }, clock.utcOffsetHours)
  const endMs = bounds(VISITOR_WINDOW_END_HOUR)
  return { startMs: bounds(VISITOR_WINDOW_START_HOUR), endMs, passed: clock.nowMs > endMs }
}

/** Medianoche del 25 de diciembre en la zona del visitante. */
export function christmasMs(season: TrackerSeason, clock: ViewerClock): number {
  return localMomentToUtcMs(
    { year: season.year, dayOfMonth: 25, localHour: 0 },
    clock.utcOffsetHours
  )
}

/**
 * Días de calendario que faltan para Navidad en la zona del visitante — el
 * número que la gente busca ("cuántos días faltan para Navidad").
 *
 * No es `duración / 86.400.000`: esa cuenta da 144 el 2 de agosto y 145 dos
 * horas después según se redondee, y en Nochebuena daría cero cuando todavía
 * queda un día. Aquí se restan **fechas**, no instantes.
 */
export function calendarDaysToChristmas(clock: ViewerClock, season: TrackerSeason): number {
  const local = new Date(clock.nowMs + clock.utcOffsetHours * HOUR_MS)
  const today = Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate())
  return Math.max(0, Math.round((Date.UTC(season.year, 11, 25) - today) / DAY_MS))
}

/** Última parada alcanzada, siguiente destino y tramo en curso. */
interface Waypoints {
  lastStop: RouteStop | null
  nextStop: RouteStop | null
  currentLeg: RouteLegKey
}

function resolveWaypoints(index: number): Waypoints {
  const lastStop = index >= 0 ? ROUTE[index] : null
  // Antes de despegar (index = −1) el trineo ya está en ROUTE[0] (el Polo
  // Norte), así que el próximo *destino* es la primera entrega, no su casa.
  const nextIndex = index < 0 ? 1 : index + 1
  const nextStop = nextIndex < ROUTE.length ? ROUTE[nextIndex] : null

  // Mientras vuela hacia el siguiente destino, el tramo en curso es el del
  // destino: es la información útil ("va camino de Europa"), no de dónde salió.
  const currentLeg: RouteLegKey = lastStop ? (nextStop?.leg ?? lastStop.leg) : 'north-pole'

  return { lastStop, nextStop, currentLeg }
}

/**
 * A qué apunta la cuenta atrás grande según la fase:
 * - preparativos, despegue y misión cumplida → la llegada a la zona del
 *   visitante (en «misión cumplida», la del año que viene: ver `windowSeason`);
 * - recorrido → su llegada si aún no ha pasado, y si ya pasó, la vuelta a casa.
 */
function resolveCountdownTarget(context: {
  phase: TrackerPhase
  season: TrackerSeason
  visitorWindow: VisitorWindow
}): number {
  const { phase, season, visitorWindow } = context
  if (phase === 'traveling' && visitorWindow.passed) {
    return season.tripEndMs
  }
  return visitorWindow.startMs
}

/** Estado completo del tracker tal y como lo ve un visitante en un instante. */
export function computeTrackerState(clock: ViewerClock): TrackerState {
  const season = resolveSeason(clock)
  const phase = resolvePhase(clock, season)
  const { index, position } = interpolatePosition(arrivalsFor(season), clock)
  const { lastStop, nextStop, currentLeg } = resolveWaypoints(index)

  // Con el recorrido ya terminado, la franja que le interesa al visitante es la
  // del año que viene. Si se dejara la de esta Navidad, la tarjeta de la
  // estimación seguiría diciendo «ya ha pasado por tu zona, mira debajo del
  // árbol» el 30 de diciembre, mientras la cuenta atrás de al lado marca 360
  // días.
  const windowSeason = phase === 'completed' ? seasonFor(season.year + 1) : season
  const visitorWindow = visitorWindowFor(windowSeason, clock)
  const countdownTargetMs = resolveCountdownTarget({ phase, season, visitorWindow })

  return {
    nowMs: clock.nowMs,
    phase,
    season,
    progress: clamp01((clock.nowMs - season.tripStartMs) / (season.tripEndMs - season.tripStartMs)),
    stopIndex: index,
    visitedCount: index + 1,
    lastStopId: lastStop?.id ?? null,
    nextStopId: phase === 'completed' ? null : (nextStop?.id ?? null),
    currentLeg,
    position,
    countdownTargetMs,
    countdownMs: Math.max(0, countdownTargetMs - clock.nowMs),
    visitorWindow,
    msToChristmas: Math.max(0, christmasMs(windowSeason, clock) - clock.nowMs),
    countriesVisited: CUMULATIVE_COUNTRIES[Math.max(0, index)],
  }
}

/** Estado de una parada respecto al trineo, para el mapa y la lista textual. */
export type StopStatus = 'visited' | 'current' | 'upcoming'

export function stopStatusAt(stop: RouteStop, state: TrackerState): StopStatus {
  const stopIndex = ROUTE.indexOf(stop)
  if (state.stopIndex < 0) return 'upcoming'
  if (stopIndex < state.stopIndex) return 'visited'
  if (stopIndex === state.stopIndex) return state.phase === 'completed' ? 'visited' : 'current'
  return 'upcoming'
}
